"use client";
import React from "react";
import gs, { colors } from "gstyles";
import { Point } from "components/common";
import { get } from "utils/lodash";
import { match } from "utils/renderPropsComposer";
interface User {
  id?: string;
  name?: string;
}
export interface ReviewProps {
  style?: React.CSSProperties;
  booking?: any;
  className?: string;
  populatedUser?: User;
  createdAt?: string;
  comment?: string;
  rating?: number;
  type?: string;
  status?: "approved" | "rejected" | "pending";

  // Handlers
  onClick?: () => void;
}

export default function Review({
  style = {},
  className = "",
  populatedUser,
  createdAt = "",
  comment = "",
  rating = 0,
  status,
  booking,
  type,
  onClick,
  ...props
}: ReviewProps) {
  const ref = React.useRef({ numOfLines: 0, firstLayout: true });
  const [showBtnMore, setShowBtnMore] = React.useState(false);
  const [showComment, setShowComment] = React.useState(false);
  const textRef = React.useRef<HTMLParagraphElement>(null);
  const user = get(populatedUser, "0") || populatedUser;
  const booked = !!booking || type === "fake";
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const checkTextOverflow = React.useCallback(() => {
    if (textRef.current && ref.current.firstLayout) {
      const element = textRef.current;
      if (element.scrollHeight > element.clientHeight) {
        setShowBtnMore(true);
        ref.current.firstLayout = false;
        ref.current.numOfLines = 3;
      }
    }
  }, []);

  React.useEffect(() => {
    if (comment) {
      checkTextOverflow();
    }
  }, [comment, checkTextOverflow]);

  const handleShowComment = () => {
    ref.current.numOfLines = 0;
    setShowComment(true);
  };

  const handleHideComment = () => {
    ref.current.numOfLines = 3;
    setShowComment(false);
  };
  const toggleComment = () => {
    if (showComment) {
      handleHideComment();
    } else {
      handleShowComment();
    }
  };
  const containerOpacity =
    match({
      isTrue: status === "rejected" || status === "pending",
      value: "opacity-60",
    }) || "";

  return (
    <div
      className={`w-[343px] relative p-4 rounded-xl border border-gray-200 bg-white cursor-pointer hover:shadow-sm transition-shadow ${containerOpacity} ${className}`}
      style={style}
      onClick={onClick}
      {...props}
    >
      {/* Header với badge status và rating */}
      <div className="flex justify-between items-start mb-3">
        {/* Badge status */}
        {booked ? (
          <div className="flex items-center gap-1.5">
            {gs.icons({
              name: "calendar",
              width: 16,
              color: colors.excellent,
            })}
            <span
              className="text-sm font-semibold"
              style={{ color: colors.excellent }}
            >
              Đã đặt lịch
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            {gs.icons({
              name: "calendar",
              width: 16,
              color: colors.gray,
            })}
            <span
              className="text-sm font-semibold"
              style={{ color: colors.gray }}
            >
              Chưa đặt lịch
            </span>
          </div>
        )}

        {/* Rating */}
        <Point value={rating} position="tr" />
      </div>

      {/* Thông tin user */}
      <div className="flex items-center gap-2 mb-2">
        <span className="font-semibold text-gray-900">
          {get(user, "name", "Người dùng")}
        </span>
        <div
          className="w-1 h-1 rounded-full bg-gray-400"
          style={{ opacity: 0.8 }}
        />
        <span className="text-sm text-gray-600" style={{ opacity: 0.8 }}>
          {formatDate(createdAt)}
        </span>
      </div>

      {/* Nội dung comment */}
      {comment && (
        <div className="mt-2">
          <p
            ref={textRef}
            className={`text-sm text-gray-700 leading-relaxed ${
              !showComment ? "line-clamp-3" : ""
            }`}
            style={{ opacity: 0.7 }}
          >
            {comment}
          </p>

          {/* Button xem thêm/thu gọn */}
          {showBtnMore && (
            <button
              className="mt-1 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors bg-transparent border-none p-0 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                toggleComment();
              }}
            >
              {showComment ? "thu gọn" : "xem thêm"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
